/** BulkCreateDistributorsDialog — full workflow for the bulk flow.
 *
 * Flow:
 *   1. User pastes agency-name list OR clicks "All agencies"
 *   2. Dry-run → backend returns 4 buckets (auto_creatable, needs_selection,
 *      already_exists, not_found)
 *   3. For each `needs_selection` agency, open a sequential dialog asking user
 *      to pick Indane / Bharat Gas / HP Gas (one-by-one per user choice #3-F)
 *   4. Final "Create" action → backend creates all + returns Excel download
 */
import { useState } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Upload, MagnifyingGlass, CheckCircle, XCircle, WarningCircle, DownloadSimple, CaretRight } from '@phosphor-icons/react';

const COMPANIES = ['Indane', 'Bharat Gas', 'HP Gas'];

export default function BulkCreateDistributorsDialog({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState('input');  // input | dry | select | creating | done
  const [pasteText, setPasteText] = useState('');
  const [useAll, setUseAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dry, setDry] = useState(null);
  const [selections, setSelections] = useState({}); // { agency_id: 'Indane' | ... }
  const [ambIndex, setAmbIndex] = useState(0);

  const reset = () => {
    setStep('input'); setPasteText(''); setUseAll(false);
    setDry(null); setSelections({}); setAmbIndex(0);
  };

  const runDryRun = async () => {
    setLoading(true);
    try {
      let payload;
      if (useAll) {
        payload = { all_agencies: true };
      } else {
        // Parse on backend for consistency
        const parse = await api.post('/distributors/bulk/parse-paste', { text: pasteText });
        const names = parse.data.agency_names || [];
        if (names.length === 0) {
          toast.error('Koi agency name detect nahi hua. Text paste kar k fir try karo.');
          setLoading(false);
          return;
        }
        payload = { agency_names: names };
      }
      const r = await api.post('/distributors/bulk/dry-run', payload);
      setDry(r.data);
      // Pre-fill auto selections
      const preSel = {};
      (r.data.auto_creatable || []).forEach(a => { preSel[a.agency_id] = a.company; });
      setSelections(preSel);
      if ((r.data.needs_selection || []).length > 0) {
        setAmbIndex(0);
        setStep('select');
      } else {
        setStep('dry');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dry-run failed');
    } finally {
      setLoading(false);
    }
  };

  const needs = dry?.needs_selection || [];
  const currentAmb = needs[ambIndex];

  const pickCompany = (company) => {
    if (!currentAmb) return;
    setSelections(prev => ({ ...prev, [currentAmb.agency_id]: company }));
    if (ambIndex + 1 < needs.length) {
      setAmbIndex(ambIndex + 1);
    } else {
      // All ambiguous resolved — go to final review
      setStep('dry');
    }
  };

  const doCreate = async () => {
    const agencies = [
      ...(dry.auto_creatable || []).map(a => ({ agency_id: a.agency_id, company: selections[a.agency_id] || a.company })),
      ...(dry.needs_selection || []).map(a => ({ agency_id: a.agency_id, company: selections[a.agency_id] })),
    ].filter(x => x.company);

    if (agencies.length === 0) {
      toast.error('Kuch bhi create karne ke liye nahi bacha');
      return;
    }
    setLoading(true);
    setStep('creating');
    try {
      // Use create-excel to get file immediately
      const res = await api.post('/distributors/bulk/create-excel', { agencies }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `distributors_bulk_created_${Date.now()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${agencies.length} distributor(s) created + Excel downloaded`);
      setStep('done');
      onCreated && onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk create failed');
      setStep('dry');
    } finally {
      setLoading(false);
    }
  };

  const close = () => { reset(); onOpenChange(false); };

  // Color + keyword summary for ambiguity popup
  const companyColor = (c) => c === 'Indane' ? 'bg-red-600 hover:bg-red-700' : c === 'Bharat Gas' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bulk-create-dist-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={22} weight="duotone" className="text-emerald-700" />
            Bulk Create Distributor IDs
          </DialogTitle>
          <p className="text-xs text-gray-600 mt-1">
            Smart company detection + strict no-duplicate rule. Default password: <b>Patna@123</b>
          </p>
        </DialogHeader>

        {step === 'input' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs space-y-1">
              <div className="font-semibold text-blue-900">Kaise use karein:</div>
              <ul className="list-disc ml-5 text-blue-800">
                <li>Niche textarea me agency names paste karo (numbers/cylinders ignore ho jayenge)</li>
                <li>OR "All Agencies" check karke saari agencies ke liye create karo</li>
                <li>Ambiguous names (e.g., "AHMEDGARH GAS AGENCY") ke liye system aapse company puchhega</li>
                <li>Already existing distributors SKIP honge — koi duplicate nahi banega</li>
              </ul>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={useAll}
                onChange={(e) => setUseAll(e.target.checked)}
                data-testid="bulk-all-agencies-check"
              />
              All Agencies ke liye create karo (textarea ignore hoga)
            </label>
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={`22.04.2026\nHARSHIL GAS\t32\nGAS GHAR BHARAT\t16\nAHMEDGARH GAS AGENCY\t100\nROHINI INDANE\t100`}
              rows={10}
              disabled={useAll}
              data-testid="bulk-paste-textarea"
              className="font-mono text-xs"
            />
            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button
                onClick={runDryRun}
                disabled={loading || (!useAll && !pasteText.trim())}
                data-testid="bulk-dry-run-btn"
                className="bg-blue-700 hover:bg-blue-800"
              >
                <MagnifyingGlass size={14} weight="bold" className="mr-1" />
                {loading ? 'Checking…' : 'Check & Preview'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'select' && currentAmb && (
          <div className="space-y-4" data-testid="bulk-ambiguity-dialog">
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <WarningCircle size={22} weight="fill" className="text-amber-600" />
                <span className="font-semibold text-amber-900 text-sm">
                  Company detect nahi ho paa rahi — please select
                </span>
                <span className="ml-auto text-xs text-amber-700 font-mono">
                  {ambIndex + 1} of {needs.length}
                </span>
              </div>
              <div className="bg-white rounded p-3 mb-4">
                <div className="text-[11px] text-gray-500 uppercase">Agency Name</div>
                <div className="text-lg font-bold text-gray-900" data-testid="bulk-amb-agency-name">
                  {currentAmb.agency_name}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Email: <span className="font-mono">{currentAmb.email}</span>
                </div>
              </div>
              <div className="text-xs font-semibold text-amber-900 mb-2">Company select karo:</div>
              <div className="grid grid-cols-3 gap-2">
                {COMPANIES.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => pickCompany(c)}
                    data-testid={`bulk-amb-pick-${c.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`${companyColor(c)} text-white rounded-md py-3 font-semibold text-sm transition`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-600">
              <div>
                Resolved: <b className="text-emerald-700">{Object.keys(selections).length}</b> /
                {(dry?.auto_creatable || []).length + needs.length}
              </div>
              <Button variant="outline" size="sm" onClick={() => { setStep('dry'); }} data-testid="bulk-skip-remaining">
                Skip & Review Below <CaretRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {step === 'dry' && dry && (
          <div className="space-y-3" data-testid="bulk-dry-review">
            <BucketCard
              title="Ready to Create"
              items={(dry.auto_creatable || []).concat((dry.needs_selection || []).filter(a => selections[a.agency_id]))}
              icon={<CheckCircle size={16} weight="fill" className="text-emerald-600" />}
              color="bg-emerald-50 border-emerald-200"
              keyFn={a => a.agency_id}
              render={a => (
                <div>
                  <div className="font-semibold">{a.agency_name}</div>
                  <div className="text-[10px] text-gray-500">
                    → {selections[a.agency_id] || a.company} • {a.email}
                  </div>
                </div>
              )}
              testid="bulk-ready-list"
            />
            <BucketCard
              title="Skipped — already exists"
              items={dry.already_exists || []}
              icon={<XCircle size={16} weight="fill" className="text-gray-500" />}
              color="bg-gray-50 border-gray-200"
              keyFn={a => a.agency_id}
              render={a => (
                <div>
                  <div className="font-semibold">{a.agency_name}</div>
                  <div className="text-[10px] text-gray-500">Existing: {a.distributor_email}</div>
                </div>
              )}
              testid="bulk-skipped-list"
            />
            {(dry.needs_selection || []).filter(a => !selections[a.agency_id]).length > 0 && (
              <BucketCard
                title="Still need company selection"
                items={(dry.needs_selection || []).filter(a => !selections[a.agency_id])}
                icon={<WarningCircle size={16} weight="fill" className="text-amber-500" />}
                color="bg-amber-50 border-amber-200"
                keyFn={a => a.agency_id}
                render={a => <div className="font-semibold">{a.agency_name}</div>}
                testid="bulk-pending-list"
              />
            )}
            {(dry.not_found || []).length > 0 && (
              <BucketCard
                title="Not found in agencies"
                items={dry.not_found.map(n => ({ name: n }))}
                icon={<XCircle size={16} weight="fill" className="text-red-500" />}
                color="bg-red-50 border-red-200"
                keyFn={a => a.name}
                render={a => <div className="font-mono text-xs">{a.name}</div>}
                testid="bulk-notfound-list"
              />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('input')}>Back</Button>
              {(dry.needs_selection || []).filter(a => !selections[a.agency_id]).length > 0 && (
                <Button variant="outline" onClick={() => { setAmbIndex(0); setStep('select'); }}>
                  Resolve Ambiguity
                </Button>
              )}
              <Button
                onClick={doCreate}
                disabled={loading || ((dry.auto_creatable || []).concat((dry.needs_selection || []).filter(a => selections[a.agency_id]))).length === 0}
                data-testid="bulk-final-create-btn"
                className="bg-emerald-700 hover:bg-emerald-800"
              >
                <DownloadSimple size={14} weight="bold" className="mr-1" />
                Create &amp; Download Excel
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'creating' && (
          <div className="p-12 text-center text-sm text-gray-600">Creating distributors…</div>
        )}

        {step === 'done' && (
          <div className="p-8 text-center space-y-3">
            <CheckCircle size={48} weight="duotone" className="mx-auto text-emerald-600" />
            <div className="text-lg font-bold text-gray-900">Bulk Creation Complete</div>
            <div className="text-xs text-gray-600">Excel file download ho chuka hai.</div>
            <DialogFooter>
              <Button onClick={close} className="bg-emerald-700 hover:bg-emerald-800" data-testid="bulk-done-close">Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BucketCard({ title, items, icon, color, keyFn, render, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={`${color} border rounded-lg p-3`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold text-gray-800 mb-2">
        {icon}
        <span>{title}</span>
        <span className="ml-auto bg-white border px-2 py-0.5 rounded-full font-mono">{items.length}</span>
      </div>
      <div className="bg-white rounded divide-y max-h-40 overflow-y-auto">
        {items.map((it, i) => (
          <div key={keyFn(it) || i} className="px-2 py-1.5 text-xs">{render(it)}</div>
        ))}
      </div>
    </div>
  );
}
